import {
  EducationalResource,
  EducationalResourceFilters,
  EducationalResourceList,
  EducationalResourcePayload,
} from "@/domain/educationalResource.interface";
import { axios, transformAxiosError } from "@/utils/axios.create";

const BASE = "/educational-resources";

export type EducationalResourceSearchParams = {
  search?: string;
  categoryId?: string;
  type?: string;
  status?: string;
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: string;
};

/** UI filters -> API query: 1-based page, empty filters omitted, search trimmed. */
export const toSearchParams = (
  filters: EducationalResourceFilters
): EducationalResourceSearchParams => {
  const params: EducationalResourceSearchParams = {
    pageNumber: filters.pageNumber + 1,
    pageSize: filters.pageSize,
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
  };
  const search = filters.search?.trim();
  if (search) params.search = search;
  if (filters.categoryId) params.categoryId = filters.categoryId;
  if (filters.type) params.type = filters.type;
  if (filters.status) params.status = filters.status;
  return params;
};

const search = async (
  filters: EducationalResourceFilters
): Promise<EducationalResourceList> =>
  axios
    .get(BASE, { params: toSearchParams(filters) })
    .then((res) => res.data.body as EducationalResourceList)
    .catch((err) => Promise.reject(transformAxiosError(err)));

const getById = async (id: string): Promise<EducationalResource> =>
  axios
    .get(`${BASE}/${id}`)
    .then((res) => res.data.body as EducationalResource)
    .catch((err) => Promise.reject(transformAxiosError(err)));

const create = async (
  data: EducationalResourcePayload
): Promise<EducationalResource> =>
  axios
    .post(BASE, data)
    .then((res) => res.data.body as EducationalResource)
    .catch((err) => Promise.reject(transformAxiosError(err)));

const update = async ({
  id,
  data,
}: {
  id: string;
  data: EducationalResourcePayload;
}): Promise<EducationalResource> =>
  axios
    .put(`${BASE}/${id}`, data)
    .then((res) => res.data.body as EducationalResource)
    .catch((err) => Promise.reject(transformAxiosError(err)));

/** Soft delete (BR-12). `updatedBy` travels in the body so the backend can stamp it. */
const remove = async ({
  id,
  updatedBy,
}: {
  id: string;
  updatedBy?: string;
}): Promise<void> =>
  axios
    .delete(`${BASE}/${id}`, { data: { updatedBy } })
    .then(() => undefined)
    .catch((err) => Promise.reject(transformAxiosError(err)));

export const EducationalResourcesService = {
  search,
  getById,
  create,
  update,
  remove,
};
